import { Link } from "react-router-dom";
import { Shield, Check, Phone, Star, MapPin, Clock, DollarSign } from "lucide-react";
import PestOnlyCalculator from "../components/PestOnlyCalculator";
import { Logo } from "../components/Layout";
import { Header } from "../components/Layout";
import IpswichBreadcrumb from "../components/IpswichBreadcrumb";

const suburbs = ["Ipswich CBD", "Brassall", "Booval", "Bundamba", "Goodna", "Redbank Plains", "Collingwood Park", "Bellbird Park", "Ripley", "Springfield", "North Ipswich", "East Ipswich", "Leichhardt", "Woodend", "Raceview"];

export default function CheapPestControlIpswichPage() {
  return (
    <>
      <>
        <title>Cheap Pest Control Ipswich | Affordable & Professional | From $165</title>
        <meta name="description" content="Affordable pest control in Ipswich QLD from $165. No hidden fees, transparent pricing. Professional treatments for cockroaches, ants, spiders & more. QBCC licensed. Get an instant quote." />
        <link rel="canonical" href="https://pestfreebrisbane.com.au/cheap-pest-control-ipswich" />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        <IpswichBreadcrumb current="Affordable Pest Control" />
        <section className="bg-gradient-to-br from-[#0d1b2a] via-[#1b2838] to-[#7ABC32]/15 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#7ABC32]/20 border border-[#7ABC32]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#7ABC32]" />
                  <span className="text-sm font-medium text-[#7ABC32]">Ipswich QLD</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight" data-testid="cheap-pest-h1">
                  Cheap Pest Control <span className="text-[#7ABC32]">Ipswich</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  Professional pest control doesn't have to break the bank. We offer Ipswich's most competitive pest control prices without cutting corners — same professional-grade products, licensed technicians and warranty as the big companies, at a fraction of the cost.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />From $165</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />No Hidden Fees</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#7ABC32]" />QBCC Licensed</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#7ABC32] hover:bg-[#6AAD28] text-white font-semibold px-6 py-3 rounded-full transition-all" data-testid="cheap-pest-call-btn">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16" id="calculator">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-800">See Our Prices — No Surprises</h2>
              <p className="text-slate-600 mt-2">Transparent, upfront pricing for Ipswich homes</p>
            </div>
            <PestOnlyCalculator source="cheap_pest_ipswich" />
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-3">Affordable Doesn't Mean Cheap Quality</h2>
            <p className="text-slate-600 text-center mb-10 max-w-3xl mx-auto">We keep our prices low by operating lean — no call centres, no franchise fees, no fleet of marked vehicles. Just experienced technicians doing quality work.</p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Same Products", desc: "We use the same professional-grade, Australian-registered chemicals as major pest control companies. No diluted products, no shortcuts.", icon: Shield, color: "#7ABC32" },
                { title: "Licensed & Insured", desc: "QBCC licensed (#15366887), fully insured and compliant with all Queensland pest control regulations. Your property is in safe hands.", icon: Shield, color: "#5BC0DE" },
                { title: "Warranty Included", desc: "Every treatment comes with a warranty — 6 months for standard, 12 months for comprehensive. If pests come back within warranty, so do we. Free of charge.", icon: Shield, color: "#E74C3C" },
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-200 text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: item.color + '15' }}>
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">How We Keep Prices Low in Ipswich</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "No Franchise Fees", desc: "We're an independent, locally owned Ipswich business. No franchise royalties or corporate overheads being passed on to you.", icon: DollarSign, color: "#7ABC32" },
                { title: "Direct Booking", desc: "You deal directly with us — no call centres, no middlemen. This keeps our costs down and means faster response times for Ipswich customers.", icon: Phone, color: "#5BC0DE" },
                { title: "Local to Ipswich", desc: "We're based in the Ipswich region, so there's no travel surcharges or long drive times adding to your bill. We service all Ipswich suburbs efficiently.", icon: MapPin, color: "#E74C3C" },
                { title: "Transparent Pricing", desc: "Our online calculator shows you the exact price before you book. No inspection fees, no call-out charges, no hidden extras. The price you see is the price you pay.", icon: DollarSign, color: "#D2691E" },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-xl bg-white border border-slate-200">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.color + '15' }}>
                    <item.icon className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Price Guide — Ipswich Pest Control</h2>
            <div className="max-w-2xl mx-auto">
              <div className="bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden">
                <div className="bg-[#7ABC32] text-white p-4 text-center">
                  <h3 className="font-bold text-lg">Standard Pest Treatment</h3>
                  <p className="text-sm text-white/80">Cockroaches, ants, spiders, silverfish — 6 month warranty</p>
                </div>
                <div className="divide-y divide-slate-200">
                  {[
                    { beds: "1 Bedroom", house: "$165", unit: "$150" },
                    { beds: "2 Bedroom", house: "$175", unit: "$170" },
                    { beds: "3 Bedroom", house: "$198", unit: "$190" },
                    { beds: "4 Bedroom", house: "$220", unit: "$210" },
                    { beds: "5 Bedroom", house: "$250", unit: "$240" },
                  ].map((row, i) => (
                    <div key={i} className="grid grid-cols-3 p-3 text-sm">
                      <span className="font-medium text-slate-800">{row.beds}</span>
                      <span className="text-center text-slate-600">House: {row.house}</span>
                      <span className="text-center text-slate-600">Unit: {row.unit}</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 bg-[#7ABC32]/5 text-center">
                  <p className="text-xs text-slate-500">Prices include GST. 10% off currently applied. Double storey +$30.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Cheap Pest Control FAQ — Ipswich</h2>
            <div className="space-y-4">
              {[
                { q: "Is cheap pest control as effective as expensive options?", a: "Yes. The products we use are the same professional-grade, Australian-registered chemicals used by major pest control companies. The difference is in our overheads, not our quality. We're owner-operated with no franchise fees, which is why we can offer lower prices." },
                { q: "What's the cheapest pest control option for my Ipswich home?", a: "Our standard pest treatment starts from $165 for a 1-bedroom home and $198 for a 3-bedroom home. This includes internal and external spray treatment for cockroaches, ants, spiders and silverfish with a 6-month warranty." },
                { q: "Are there any hidden fees?", a: "No. Our online calculator shows you the exact price before you book. There are no call-out fees, no inspection charges and no surprise extras. The quote price is the final price." },
                { q: "Do you offer discounts for regular treatments?", a: "Yes. We currently offer 10% off all services as a launch special. We also offer discounted rates for 6-monthly and annual treatment plans, which provide the best long-term value and pest protection for Ipswich homes." },
                { q: "Can I get same-day pest control at these prices?", a: "Yes. Same-day and next-day bookings are available at no extra cost across all Ipswich suburbs. Our competitive prices apply regardless of how quickly you need the service." },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-slate-200">
                  <h3 className="font-bold text-slate-800 mb-2">{item.q}</h3>
                  <p className="text-sm text-slate-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Affordable Pest Control Across Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-slate-50 border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding areas in the Ipswich region</p>
          </div>
        </section>

        <section className="py-8 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-lg font-bold text-slate-800 text-center mb-4">Related Services in Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/pest-control-ipswich" className="text-sm text-[#E74C3C] hover:underline font-medium">Pest Control Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/cockroach-treatment-ipswich" className="text-sm text-[#E74C3C] hover:underline font-medium">Cockroach Treatment Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/end-of-lease-pest-control-ipswich" className="text-sm text-[#7ABC32] hover:underline font-medium">End of Lease Pest Control</Link>
              <span className="text-slate-300">|</span>
              <Link to="/termite-inspection-ipswich" className="text-sm text-[#D2691E] hover:underline font-medium">Termite Inspection Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/bird-control-ipswich" className="text-sm text-[#5BC0DE] hover:underline font-medium">Bird Control Ipswich</Link>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gradient-to-r from-[#0d1b2a] to-[#7ABC32] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Quality Pest Control at Ipswich's Best Prices</h2>
            <p className="text-lg text-white/80 mb-6">No franchise fees, no call centres — just honest pricing and professional results.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-[#0d1b2a] font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
                <Phone className="w-5 h-5" /> Call (07) 3708 1422
              </a>
              <a href="#calculator" className="inline-flex items-center gap-2 border-2 border-white text-white font-bold px-8 py-3 rounded-full hover:bg-white/10 transition-all">
                <Clock className="w-5 h-5" /> See Prices
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
